import { useEffect, useMemo, useState } from 'react';

import { Flex } from '@/components/Flex';
import { ImagePlaceholder } from '@/components/ImagePlaceholder';
import { Price } from '@/components/Price';
import { Typography } from '@/components/Typography';
import type { TaxedMoney } from '@/graphql/saleor/api';
import { useStrapSizesQuery } from '@/graphql/saleor/api';
import type { AllSelectedProducts } from '@/hooks/useAllSelectedProductsState';
import type {
  StrapAddToBagBuckle,
  StrapAddToBagTool,
} from '@/hooks/useBucklesAndTools';
import { getBoolValue, getMultiselectValue } from '@/lib/attributes';
import { STRAP_SIZES_FETCH_LIMIT } from '@/lib/const';
import { mapEdgesToItems } from '@/lib/maps';
import { type ProductTypeOptions } from '@/lib/product';
import type { SetState } from '@/lib/types';
import { useRegionProvider } from '@/providers/RegionProvider';
import { px2rem } from '@/theme';
import type { StrapAddToBagStrap } from '@/views/ShoppingBagView/components/ShoppingBagStrapModal';

import * as M from './messages';
import * as S from './ProductItem.styles';

interface CommonProductItemProps {
  data: StrapAddToBagBuckle | StrapAddToBagTool | StrapAddToBagStrap;
  productType: ProductTypeOptions;
  selectedSku: null | string;
}

interface ProductItemClickable extends CommonProductItemProps {
  isClickable: true;
  isDeselectable?: boolean;
  nbBuckles?: number;
  onClick?: () => void;
  setAllSelectedProducts: SetState<AllSelectedProducts>;
  setSelectedSku: SetState<null | string>;
}

interface ProductItemNotClickable extends CommonProductItemProps {
  isClickable: false;
  isDeselectable?: never;
  nbBuckles?: number;
  onClick?: never;
  setAllSelectedProducts?: never;
  setSelectedSku?: never;
}

export type ProductItemProps = (
  | ProductItemClickable
  | ProductItemNotClickable
) & {
  translatedStrapNames?: Record<string, string>;
};

export const ProductItem = ({
  data,
  isClickable,
  isDeselectable = true,
  nbBuckles,
  onClick,
  productType,
  selectedSku,
  setAllSelectedProducts,
  setSelectedSku,
  translatedStrapNames,
}: ProductItemProps) => {
  const { query } = useRegionProvider();
  const { pricing, product, sku } = data;

  // We get the right id for the strap size because the one in data is the Standard one...
  const { data: dataStrapSizes, loading: loadingStrapSizes } =
    useStrapSizesQuery({
      variables: {
        ...query,
        filter: {
          sku: [sku ?? ''],
        },
        limit: STRAP_SIZES_FETCH_LIMIT,
      },
      fetchPolicy: 'cache-first',
      skip: productType !== 'strap',
    });

  const id =
    productType === 'strap'
      ? mapEdgesToItems(dataStrapSizes?.productVariants)?.[0]?.id
      : data.id;

  const [strapName, setStrapName] = useState<string>();
  const productName = useMemo(() => {
    let name;
    switch (productType) {
      case 'buckle':
        name = getMultiselectValue(
          (product as StrapAddToBagBuckle['product']).buckleName,
        )[0];
        break;
      case 'strap':
        name = getMultiselectValue(
          (product as StrapAddToBagStrap['product']).strapName,
        )[0];
        break;
      case 'tool':
        name = getMultiselectValue(
          (product as StrapAddToBagTool['product']).toolName,
        )[0];
        break;
    }
    return name ?? strapName ?? product.name;
  }, [productType, product]);
  useEffect(() => {
    if (sku && translatedStrapNames) {
      setStrapName(translatedStrapNames[sku]);
    }
  }, [translatedStrapNames, sku]);
  const productPrice = pricing?.price;
  const selected = selectedSku === sku;
  const hasIntegratedBuckle =
    productType === 'strap' &&
    getBoolValue((data as StrapAddToBagStrap).product.integratedBuckle);

  const handleClick = (id: string, productPrice: TaxedMoney, sku: string) => {
    onClick && onClick();

    if (selectedSku === sku) {
      if (!isDeselectable) {
        return;
      }
      setSelectedSku?.(null);
      setAllSelectedProducts?.((curr) => {
        return {
          totalPrice: {
            ...curr.totalPrice,
            gross: {
              ...curr.totalPrice.gross,
              amount: curr.totalPrice.gross.amount - productPrice.gross.amount,
            },
            net: {
              ...curr.totalPrice.net,
              amount: curr.totalPrice.net.amount - productPrice.net.amount,
            },
          },
          products: curr.products.filter((product) => product.id !== id),
        } satisfies AllSelectedProducts;
      });
      return;
    }

    if (selectedSku) {
      setSelectedSku?.(sku);
      setAllSelectedProducts?.((curr) => {
        const previouslySelectedProduct = curr.products.find(
          (product) => product.productTypeSlug === productType,
        );

        const productsWithoutPreviouslySelected = curr.products.filter(
          (product) => product.productTypeSlug !== productType,
        );

        return {
          products: [
            ...productsWithoutPreviouslySelected,
            {
              id,
              price: productPrice,
              productTypeSlug: productType,
              sku,
            },
          ],
          totalPrice: {
            ...curr.totalPrice,
            gross: {
              ...curr.totalPrice.gross,
              amount:
                curr.totalPrice.gross.amount +
                productPrice.gross.amount -
                (previouslySelectedProduct?.price.gross.amount ?? 0),
            },
            net: {
              ...curr.totalPrice.net,
              amount:
                curr.totalPrice.net.amount +
                productPrice.net.amount -
                (previouslySelectedProduct?.price.net.amount ?? 0),
            },
          },
        } satisfies AllSelectedProducts;
      });

      return;
    }

    setSelectedSku?.(sku);
    setAllSelectedProducts?.(
      (curr) =>
        ({
          products: [
            ...curr.products,
            {
              id,
              sku,
              price: productPrice,
              productTypeSlug: productType,
            },
          ],
          totalPrice: {
            ...curr.totalPrice,
            currency: productPrice.currency,
            gross: {
              ...curr.totalPrice.gross,
              amount: curr.totalPrice.gross.amount + productPrice.gross.amount,
              currency: productPrice.gross.currency,
            },
            net: {
              ...curr.totalPrice.net,
              amount: curr.totalPrice.net.amount + productPrice.net.amount,
              currency: productPrice.net.currency,
            },
            tax: {
              ...curr.totalPrice.tax,
              amount: curr.totalPrice.tax.amount + productPrice.tax.amount,
              currency: productPrice.tax.currency,
            },
          },
        } satisfies AllSelectedProducts),
    );
  };

  return (
    <S.Wrapper
      $isClickable={isClickable && !loadingStrapSizes}
      $nbBuckles={nbBuckles ?? 0}
      $productType={productType}
      $selected={selected}
      data-testid='productItemWrapper'
      onClick={() =>
        isClickable &&
        !loadingStrapSizes &&
        productPrice &&
        productPrice.gross.amount >= 0 &&
        sku
          ? handleClick(id, productPrice, sku)
          : undefined
      }
    >
      <S.ImageWrapper
        $productType={productType}
        className='image-wrapper'
        data-test-id='ProductItemImageWrapper'
      >
        {product.thumbnail ? (
          <S.ProductImage
            $productType='buckle'
            alt={product.thumbnail.alt!}
            data-test-id='ProductItemImage'
            fill
            productType={productType}
            sizes='30vw'
            src={product.thumbnail.url}
          />
        ) : (
          <ImagePlaceholder
            className={`shopping-bag-${productType}-card`}
            data-test-id='ProductItemPlaceholder'
            productType={productType}
          />
        )}
      </S.ImageWrapper>
      {productType !== 'buckle' ? (
        <Flex
          alignItems='flex-start'
          data-test-id='ProductItemFlexWrapper'
          flexDirection='column'
          justifyContent='center'
        >
          <Typography
            color='secondary'
            data-test-id='ProductItemProductName'
            fontFamily='italianPlate'
            scale='p1'
            textTransform='uppercase'
          >
            {productName}
          </Typography>
          {productType !== 'tool' && (
            <Typography
              color='secondaryGrey'
              data-test-id='ProductItemSku'
              fontFamily='italianPlate'
              scale='p2'
              style={{ marginTop: px2rem(2) }}
            >
              {sku}
            </Typography>
          )}
          {productType === 'strap' && !hasIntegratedBuckle && (
            <Typography
              color='secondary'
              data-test-id='ProductItemBuckleNotIncluded'
              fontFamily='italianPlate'
              scale='p2'
              style={{ marginTop: px2rem(2) }}
            >
              {M.BuckleNotIncluded}
            </Typography>
          )}
          <Typography
            as='div'
            color='secondary'
            data-test-id='ProductItemPrice'
            scale='p2'
          >
            <Price
              color='secondary'
              data-test-id='ProductItemPriceComponent'
              price={pricing?.price?.gross}
              size='inherited'
            />
          </Typography>
        </Flex>
      ) : (
        <Flex
          alignItems={nbBuckles && nbBuckles <= 1 ? 'flex-start' : 'center'}
          data-test-id='ProductItemBuckleFlexWrapper'
          flexDirection='column'
          justifyContent='center'
        >
          <S.BuckleName
            color='secondary'
            data-test-id='ProductItemBuckleName'
            fontFamily='italianPlate'
            scale='p2'
            style={{ marginTop: px2rem(2) }}
            textAlign='center'
            textTransform='uppercase'
          >
            {productName}
          </S.BuckleName>
          <Typography
            color='secondaryGrey'
            data-test-id='ProductItemBuckleSku'
            fontFamily='italianPlate'
            scale='p3'
            textAlign='center'
          >
            {sku}
          </Typography>
          <Typography
            as='div'
            color='secondary'
            data-test-id='ProductItemBucklePrice'
            scale='p3'
            textAlign='center'
          >
            <Price
              color='secondary'
              data-test-id='ProductItemBucklePriceComponent'
              price={pricing?.price?.gross}
              size='inherited'
            />
          </Typography>
        </Flex>
      )}
    </S.Wrapper>
  );
};
